// SeletorEmoji.js — a caixa de escolher emoji.
//
// ================= POR QUE NÃO UMA BIBLIOTECA =================
//
// `emoji-picker-react` e `emoji-mart` resolvem isto — e trazem de 1 a 40 MB de
// imagens e dados para desenhar o que a fonte do sistema já desenha nativamente.
// Aqui a lista é texto puro gerado do próprio Unicode (src/config/emojis.js,
// 8 KB, ~1.200 emojis) e os emojis são renderizados como caractere: o Windows,
// o Android e o iPhone desenham cada um com a sua fonte, que é como a pessoa já
// os vê no resto do aparelho.
//
// O QUE A LISTA NÃO TEM: bandeiras, tons de pele e sequências com juntor
// (família, profissões) — elas são combinações, não caracteres isolados. Por
// isso existe o campo de COLAR: qualquer emoji que o teclado do aparelho
// produza (Windows: Win + ponto; celular: a tecla de emoji) entra por ali.
//
// A busca por nome ficou de fora de propósito: os nomes em português seriam
// mais dados do que a lista inteira, para um seletor que se varre com o olho em
// oito abas.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import EMOJIS from "../../config/emojis";

const CHAVE_RECENTES = "insigne:emojis-recentes";
const MAX_RECENTES = 16;

const lerRecentes = () => {
  try {
    const bruto = JSON.parse(localStorage.getItem(CHAVE_RECENTES) || "[]");
    return Array.isArray(bruto) ? bruto.filter((e) => typeof e === "string").slice(0, MAX_RECENTES) : [];
  } catch {
    return [];
  }
};

const guardarRecente = (emoji) => {
  try {
    const lista = [emoji, ...lerRecentes().filter((e) => e !== emoji)].slice(0, MAX_RECENTES);
    localStorage.setItem(CHAVE_RECENTES, JSON.stringify(lista));
  } catch {
    /* navegador sem armazenamento: os recentes simplesmente não duram */
  }
};

/** O mesmo critério do servidor: emoji é o que NÃO é texto. */
export const pareceEmoji = (valor) => {
  const s = String(valor || "").trim();
  return s !== "" && [...s].length <= 16 && !/[\p{L}\p{N}\s<>&"']/u.test(s);
};

const Grade = ({ itens, atual, onEscolher }) => (
  <div className="grid grid-cols-8 sm:grid-cols-10 gap-0.5">
    {itens.map((e, i) => (
      <button
        key={e + i}
        type="button"
        onClick={() => onEscolher(e)}
        aria-label={`Escolher ${e}`}
        className={
          "flex h-9 items-center justify-center rounded-lg text-xl leading-none transition-colors " +
          (atual === e ? "bg-insigne-orange/25 ring-1 ring-insigne-orange" : "hover:bg-white/10")
        }
      >
        {e}
      </button>
    ))}
  </div>
);

const SeletorEmoji = ({ aberto, atual, onEscolher, onFechar }) => {
  const [aba, setAba] = useState(0);
  const [colado, setColado] = useState("");
  const recentes = useMemo(() => (aberto ? lerRecentes() : []), [aberto]);

  useEffect(() => {
    if (!aberto) return;
    setColado("");
    const aoTeclar = (e) => { if (e.key === "Escape") onFechar(); };
    document.addEventListener("keydown", aoTeclar);
    document.body.classList.add("menu-open");
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.classList.remove("menu-open");
    };
  }, [aberto, onFechar]);

  if (!aberto) return null;

  const escolher = (emoji) => {
    guardarRecente(emoji);
    onEscolher(emoji);
  };

  const categoria = EMOJIS[aba] || EMOJIS[0];

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center bg-black/70 px-0 sm:px-4 backdrop-blur-sm"
      onClick={onFechar}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Escolher emoji"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-painel
                   border border-line bg-insigne-navy-soft text-white shadow-2xl"
      >
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 pt-4 pb-2">
          <h2 className="font-bold">Escolher um emoji</h2>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            className="foco-laranja flex h-9 w-9 items-center justify-center rounded-full border border-line
                       bg-white/5 text-xl transition-colors hover:bg-white/10"
          >
            <i className="bi bi-x" />
          </button>
        </div>

        {/* Abas das categorias. Rolam na horizontal no celular — oito abas não
            cabem em 360px, e quebrá-las em duas linhas comeria a grade. */}
        <div className="shrink-0 flex gap-1 overflow-x-auto px-3 pb-2">
          {EMOJIS.map((c, i) => (
            <button
              key={c.nome}
              type="button"
              onClick={() => setAba(i)}
              title={c.nome}
              aria-label={c.nome}
              aria-pressed={aba === i}
              className={
                "shrink-0 rounded-lg px-3 py-1.5 text-base transition-colors " +
                (aba === i ? "bg-insigne-orange/20 text-insigne-orange" : "text-white/55 hover:bg-white/10")
              }
            >
              <i className={"bi " + c.icone} />
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-3">
          {recentes.length > 0 && aba === 0 && (
            <>
              <p className="px-1 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-white/40">
                Recentes
              </p>
              <Grade itens={recentes} atual={atual} onEscolher={escolher} />
              <div className="my-2 h-px bg-white/10" />
            </>
          )}

          <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-white/40">
            {categoria.nome}
          </p>
          <Grade itens={[...categoria.itens]} atual={atual} onEscolher={escolher} />
        </div>

        {/* Colar: a porta para bandeiras, tons de pele e sequências — tudo o
            que não é um caractere só e por isso não está na lista. */}
        <div className="shrink-0 border-t border-line px-4 py-3">
          <div className="flex items-center gap-2">
            <input
              value={colado}
              onChange={(e) => setColado(e.target.value)}
              placeholder="Ou cole um emoji aqui"
              aria-label="Colar um emoji"
              className="foco-laranja min-w-0 flex-1 rounded-lg border border-line bg-white/5 px-3 py-2 text-sm
                         text-white placeholder:text-white/35 outline-none transition-colors
                         focus:border-insigne-orange/50"
            />
            <button
              type="button"
              disabled={!pareceEmoji(colado)}
              onClick={() => escolher(colado.trim())}
              className="foco-laranja shrink-0 rounded-lg bg-insigne-orange px-3 py-2 text-sm font-bold text-paper
                         transition-colors hover:bg-insigne-orange-dark disabled:opacity-40"
            >
              Usar
            </button>
          </div>

          {atual && (
            <button
              type="button"
              onClick={() => onEscolher(null)}
              className="mt-2.5 w-full rounded-lg border border-line bg-white/5 py-2 text-sm font-semibold
                         text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <i className="bi bi-x-circle" /> Tirar o emoji deste livro
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SeletorEmoji;
